import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

type PreviewType = "watchlist" | "filter" | "tagWatchlist" | null;

interface PreviewState {
  type: PreviewType;
  id: number | null;
  tagIds?: string[]; // Tag IDs for tagWatchlist preview
}

interface PortfolioBuilderContextValue {
  previewState: PreviewState;
  setPreviewWatchlist: (id: number) => void;
  setPreviewFilter: (id: number) => void;
  setPreviewTagWatchlist: (id: number, tagIds: string[]) => void;
  clearPreview: () => void;
  isPreviewingWatchlist: (id: number) => boolean;
  isPreviewingFilter: (id: number) => boolean;
  isPreviewingTagWatchlist: (id: number) => boolean;
}

const PortfolioBuilderContext = createContext<PortfolioBuilderContextValue | null>(null);

interface PortfolioBuilderProviderProps {
  children: ReactNode;
}

export function PortfolioBuilderProvider({ children }: PortfolioBuilderProviderProps) {
  const [previewState, setPreviewState] = useState<PreviewState>({
    type: null,
    id: null,
  });

  const setPreviewWatchlist = useCallback((id: number) => {
    setPreviewState((prev) => {
      // Toggle off if clicking the same watchlist
      if (prev.type === "watchlist" && prev.id === id) {
        return { type: null, id: null };
      }
      return { type: "watchlist", id };
    });
  }, []);

  const setPreviewFilter = useCallback((id: number) => {
    setPreviewState((prev) => {
      // Toggle off if clicking the same filter
      if (prev.type === "filter" && prev.id === id) {
        return { type: null, id: null };
      }
      return { type: "filter", id };
    });
  }, []);

  const setPreviewTagWatchlist = useCallback((id: number, tagIds: string[]) => {
    setPreviewState((prev) => {
      // Toggle off if clicking the same tag watchlist
      if (prev.type === "tagWatchlist" && prev.id === id) {
        return { type: null, id: null };
      }
      return { type: "tagWatchlist", id, tagIds };
    });
  }, []);

  const clearPreview = useCallback(() => {
    setPreviewState({ type: null, id: null });
  }, []);

  const isPreviewingWatchlist = useCallback(
    (id: number) => previewState.type === "watchlist" && previewState.id === id,
    [previewState]
  );

  const isPreviewingFilter = useCallback(
    (id: number) => previewState.type === "filter" && previewState.id === id,
    [previewState]
  );

  const isPreviewingTagWatchlist = useCallback(
    (id: number) => previewState.type === "tagWatchlist" && previewState.id === id,
    [previewState]
  );

  const value: PortfolioBuilderContextValue = {
    previewState,
    setPreviewWatchlist,
    setPreviewFilter,
    setPreviewTagWatchlist,
    clearPreview,
    isPreviewingWatchlist,
    isPreviewingFilter,
    isPreviewingTagWatchlist,
  };

  return (
    <PortfolioBuilderContext.Provider value={value}>
      {children}
    </PortfolioBuilderContext.Provider>
  );
}

export function usePortfolioBuilder() {
  const context = useContext(PortfolioBuilderContext);
  if (!context) {
    throw new Error("usePortfolioBuilder must be used within a PortfolioBuilderProvider");
  }
  return context;
}

/**
 * Optional hook that returns null if not within a PortfolioBuilderProvider.
 * Use this in components that may or may not be rendered within the provider.
 */
export function usePortfolioBuilderOptional() {
  return useContext(PortfolioBuilderContext);
}

export default PortfolioBuilderContext;
